import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { BottomNav, Footer, TopNav } from './components/Layout';
import { LoadingPanel } from './components/ui';
import { useAuth } from './lib/auth';

/* Routes are lazy so the first paint ships only the shell plus the home page. */
const HomePage = lazy(() => import('./pages/HomePage'));
const BallotPage = lazy(() => import('./pages/BallotPage'));
const PollPage = lazy(() => import('./pages/PollPage'));
const ScoreboardPage = lazy(() => import('./pages/ScoreboardPage'));
const GamePage = lazy(() => import('./pages/GamePage'));
const TeamsPage = lazy(() => import('./pages/TeamsPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const VotersPage = lazy(() => import('./pages/VotersPage'));
const VoterPage = lazy(() => import('./pages/VoterPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SignInPage = lazy(() => import('./pages/SignInPage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);
  return null;
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingPanel message="Checking your credentials..." rows={3} />;
  if (!user) return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  return children;
}

function RequireAdmin({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingPanel message="Checking your credentials..." rows={3} />;
  if (!user) return <Navigate to="/signin" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

export function App() {
  return (
    <>
      <a className="fv-skip-link" href="#main">
        Skip to content
      </a>
      <ScrollToTop />
      <TopNav />
      <main id="main" className="fv-shell">
        <Suspense fallback={<LoadingPanel message="Loading FanVote25..." />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/ballot"
              element={
                <RequireAuth>
                  <BallotPage />
                </RequireAuth>
              }
            />
            <Route path="/poll" element={<PollPage />} />
            <Route path="/scoreboard" element={<ScoreboardPage />} />
            <Route path="/games/:gameId" element={<GamePage />} />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/teams/:teamId" element={<TeamPage />} />
            <Route path="/voters" element={<VotersPage />} />
            <Route path="/voters/:userId" element={<VoterPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <ProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path="/settings"
              element={
                <RequireAuth>
                  <SettingsPage />
                </RequireAuth>
              }
            />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route
              path="/welcome"
              element={
                <RequireAuth>
                  <WelcomePage />
                </RequireAuth>
              }
            />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/share/:weekNumber/:username" element={<SharePage />} />
            <Route
              path="/admin/*"
              element={
                <RequireAdmin>
                  <AdminPage />
                </RequireAdmin>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <BottomNav />
    </>
  );
}
